/**
 * AI Generator i18n
 * Internal translations for AI Content Generator components
 */

export interface AIGeneratorTranslations {
  title: string;
  titleRegenerate: string;
  questionTypeLabel: string;
  questionTypePlaceholder: string;
  promptLabel: string;
  promptPlaceholder: string;
  promptPlaceholderDisabled: string;
  generateButton: string;
  regenerateButton: string;
  generatingButton: string;
  attachButton: string;
  tipsTitle: string;
  tipSpecific: string;
  tipDifficulty: string;
  tipShortcut: string;
  errorEnterPrompt: string;
  errorSelectType: string;
  generationComplete: string;
  generationFailed: string;
  generationCancelled: string;
  generating: string;
  continueButton: string;
  dismissButton: string;
  retryButton: string;
  totalTime: string;
  checkingStatus: string;
  loading: string;
  errorLoadingTypes: string;
  previewTitle: string;
  previewPlaceholder: string;
  fullscreen: string;
  exitFullscreen: string;
  refresh: string;
  // EAA-2029: targeted chat edit
  chatEditTargetsLabel: string;
  chatEditTargetAll: string;
  chatEditQuestion: string;
  chatEditChanged: string;
  chatEditNoChange: string;
  chatEditRejected: string;
  chatEditDropped: string;
  chatEditLanguage: string;
  chatEditUndo: string;
  chatEditUndoing: string;
  chatEditUndone: string;
  chatEditUndoFailed: string;
  chatEditRegenerateConfirm: string;
  chatEditRegenerateYes: string;
  chatEditRegenerateNo: string;
  rollbackFailed: string;
  languageVi: string;
  languageEn: string;
}

const en: AIGeneratorTranslations = {
  title: 'AI Content Generator',
  titleRegenerate: 'AI Content Regenerator',
  questionTypeLabel: 'Question Type',
  questionTypePlaceholder: 'Select a question type',
  promptLabel: 'Prompt',
  promptPlaceholder: 'Describe the content you want to generate...',
  promptPlaceholderDisabled: 'Select a question type first...',
  generateButton: 'Generate',
  regenerateButton: 'Regenerate',
  generatingButton: 'Generating...',
  attachButton: 'Attach',
  tipsTitle: 'Tips:',
  tipSpecific: 'Be specific about the topic and learning objectives',
  tipDifficulty: 'Mention the difficulty level if relevant',
  tipShortcut: 'Press Ctrl+Enter to generate',
  errorEnterPrompt: 'Please enter a prompt',
  errorSelectType: 'Please select a question type',
  generationComplete: 'Generation Complete',
  generationFailed: 'Generation Failed',
  generationCancelled: 'Generation Cancelled',
  generating: 'Generating Content...',
  continueButton: 'Continue',
  dismissButton: 'Dismiss',
  retryButton: 'Retry',
  totalTime: 'Total time',
  checkingStatus: 'Checking status...',
  loading: 'Loading...',
  errorLoadingTypes: 'Error loading types',
  previewTitle: 'Preview',
  previewPlaceholder: 'Preview will appear here after content is loaded',
  fullscreen: 'Fullscreen',
  exitFullscreen: 'Exit',
  refresh: 'Refresh',
  chatEditTargetsLabel: 'Edit only:',
  chatEditTargetAll: 'Let AI decide',
  chatEditQuestion: 'Q{n}',
  chatEditChanged: 'Changed: {items}.',
  chatEditNoChange: 'No question was changed.',
  chatEditRejected: '{n} suggested change(s) were blocked to protect the set (other questions or language).',
  chatEditDropped: 'Warning: the set went from {before} to {after} questions.',
  chatEditLanguage: 'Warning: the question language changed ({before} → {after}).',
  chatEditUndo: 'Undo this edit',
  chatEditUndoing: 'Undoing…',
  chatEditUndone: 'Edit undone.',
  chatEditUndoFailed: 'Could not undo the edit.',
  chatEditRegenerateConfirm:
    'This message asks to regenerate the whole set. Every current question will be replaced (the current version stays in version history).',
  chatEditRegenerateYes: 'Regenerate all',
  chatEditRegenerateNo: 'Cancel',
  rollbackFailed: 'Could not restore this version.',
  languageVi: 'Vietnamese',
  languageEn: 'English',
};

const vi: AIGeneratorTranslations = {
  title: 'Tạo nội dung AI',
  titleRegenerate: 'Tạo lại nội dung AI',
  questionTypeLabel: 'Loại câu hỏi',
  questionTypePlaceholder: 'Chọn loại câu hỏi',
  promptLabel: 'Yêu cầu',
  promptPlaceholder: 'Mô tả nội dung bạn muốn tạo...',
  promptPlaceholderDisabled: 'Vui lòng chọn loại câu hỏi trước...',
  generateButton: 'Tạo',
  regenerateButton: 'Tạo lại',
  generatingButton: 'Đang tạo...',
  attachButton: 'Đính kèm',
  tipsTitle: 'Gợi ý:',
  tipSpecific: 'Nêu rõ chủ đề và mục tiêu học tập',
  tipDifficulty: 'Đề cập mức độ khó nếu cần',
  tipShortcut: 'Nhấn Ctrl+Enter để tạo',
  errorEnterPrompt: 'Vui lòng nhập yêu cầu',
  errorSelectType: 'Vui lòng chọn loại câu hỏi',
  generationComplete: 'Tạo hoàn tất',
  generationFailed: 'Tạo thất bại',
  generationCancelled: 'Đã hủy',
  generating: 'Đang tạo nội dung...',
  continueButton: 'Tiếp tục',
  dismissButton: 'Đóng',
  retryButton: 'Thử lại',
  totalTime: 'Tổng thời gian',
  checkingStatus: 'Đang kiểm tra...',
  loading: 'Đang tải...',
  errorLoadingTypes: 'Lỗi tải danh sách',
  previewTitle: 'Xem trước',
  previewPlaceholder: 'Nội dung xem trước sẽ hiển thị sau khi tải xong',
  fullscreen: 'Toàn màn hình',
  exitFullscreen: 'Thoát',
  refresh: 'Làm mới',
  chatEditTargetsLabel: 'Chỉ sửa:',
  chatEditTargetAll: 'Để AI xác định',
  chatEditQuestion: 'Câu {n}',
  chatEditChanged: 'Đã sửa: {items}.',
  chatEditNoChange: 'Không có câu nào được thay đổi.',
  chatEditRejected: '{n} thay đổi AI đề xuất đã bị chặn để bảo vệ bộ câu hỏi (câu khác hoặc ngôn ngữ).',
  chatEditDropped: 'Cảnh báo: bộ câu hỏi giảm từ {before} xuống {after} câu.',
  chatEditLanguage: 'Cảnh báo: ngôn ngữ câu hỏi đã đổi ({before} → {after}).',
  chatEditUndo: 'Hoàn tác lần sửa này',
  chatEditUndoing: 'Đang hoàn tác…',
  chatEditUndone: 'Đã hoàn tác.',
  chatEditUndoFailed: 'Không hoàn tác được.',
  chatEditRegenerateConfirm:
    'Tin nhắn này yêu cầu tạo lại toàn bộ bộ câu hỏi. Mọi câu hiện có sẽ bị thay thế (bản hiện tại vẫn được lưu trong Lịch sử phiên bản).',
  chatEditRegenerateYes: 'Tạo lại toàn bộ',
  chatEditRegenerateNo: 'Hủy',
  rollbackFailed: 'Không khôi phục được phiên bản này.',
  languageVi: 'Tiếng Việt',
  languageEn: 'Tiếng Anh',
};

const translations: Record<string, AIGeneratorTranslations> = {
  en,
  vi,
};

let currentLocale = 'en';

export function setLocale(locale: string): void {
  if (translations[locale]) {
    currentLocale = locale;
  }
}

export function getLocale(): string {
  return currentLocale;
}

export function t<K extends keyof AIGeneratorTranslations>(key: K): string {
  return translations[currentLocale]?.[key] || translations.en[key] || key;
}

export function useAIGeneratorTranslations(): AIGeneratorTranslations {
  return translations[currentLocale] || translations.en;
}

export default { t, setLocale, getLocale, useAIGeneratorTranslations };
